import React, { useEffect, useState, useRef } from "react";
import * as echarts from "echarts";
import { Row, Col } from "react-bootstrap";

const opciones = [
  { value: "P50", label: "Exposición P50" },
  { value: "P95", label: "Exposición P95" },
  { value: "P99", label: "Exposición P99" },
];

const formatNumber = (value) => {
  if (value >= 1e9) return (value / 1e9).toFixed(1) + "B";
  if (value >= 1e6) return (value / 1e6).toFixed(1) + "M";
  if (value >= 1e3) return (value / 1e3).toFixed(1) + "K";
  return value;
};

const clases = ["Confidencialidad", "Integridad", "Disponibilidad"];
// Calcula los datos para la gráfica
const calcularDatos = (resumen, selected) => {
  const key = `aporte_control_${selected.toLowerCase()}_introducir`;
  // Suma total de los aportes
  const total = clases.reduce((acc, clase) => {
    const valor = Number(resumen?.[clase]?.[key]) || 0;
    return acc + valor;
  }, 0);

  // Calcula porcentaje y valor para cada clase
  return clases.map((clase) => {
    const valor = Number(resumen?.[clase]?.[key]) || 0;
    const porcentaje = total > 0 ? (valor / total) * 100 : 0;
    return {
      name: `${clase}:` + formatNumber(valor),
      value: porcentaje.toFixed(2),
    };
  });
};
const GraficoComponenteRanking = ({ percentil, resultado, nombresReferencia }) => {
  useEffect(() => {
    const chartDom = document.getElementById("grafica-componente-ranking");
    if (!chartDom) return;
    const myChart = echarts.init(chartDom);

    const atributos = Object.keys(resultado[percentil]); // ["confidencialidad", "integridad", "disponibilidad"]

    // Construir series: cada aplicación será una barra en su atributo
    const series = [];
    atributos.forEach((atributo, attrIndex) => {
      const valores = resultado[percentil][atributo];
      const nombres = nombresReferencia[percentil][atributo];

      valores.forEach((valor, i) => {
        series.push({
          name: nombres[i],
          type: "bar",
          data: atributos.map((_, idx) => (idx === attrIndex ? valor : null)), // valor solo en su atributo
          barWidth: 18,
          barGap: "0%",
          barCategoryGap: "35%",
          label: {
            show: true,
            position: "right",
            formatter: () => `${nombres[i]}: ${formatNumber(valor)}`,
          },
          itemStyle: {
            color: ["#5470C6", "#91CC75", "#FAC858"][i], // colores por posición en top
          },
        });
      });
    });

    const option = {
      title: {
        text: `Top aplicaciones protegidas, Exposición ${percentil}`,
        left: "center",
      },
      tooltip: {
        trigger: "item",
        formatter: (params) => `${params.seriesName}: ${formatNumber(params.value)}`,
      },
      grid: {
        left: "5%",
        right: "20%",
        top: "12%",
        bottom: "5%",
        height: "70%",
        containLabel: true,
      },
      xAxis: { type: "value" },
      yAxis: {
        type: "category",
        data: atributos.map(a => a.charAt(0).toUpperCase() + a.slice(1)),
        inverse: true,
      },
      series,
    };

    myChart.setOption(option);

    const resizeHandler = () => myChart.resize();
    window.addEventListener("resize", resizeHandler);
    return () => {
      window.removeEventListener("resize", resizeHandler);
      myChart.dispose();
    };
  }, [percentil, resultado, nombresReferencia]);

  return (
    <div
      id="grafica-componente-ranking"
      style={{ width: "600px", height: "400px" }}
    />
  );
};

const GraficaExposicionApps = ({ resumen, selected }) => {
  const [data, setData] = useState([]);

  useEffect(() => {
    setData(calcularDatos(resumen, selected));
  }, [resumen, selected]);

  useEffect(() => {
    const chartDom = document.getElementById("grafica-exposicion-apps");
    if (!chartDom) return;
    const myChart = echarts.init(chartDom);

    const option = {
      title: {
        text: `Protección por atributo de seguridad, Exposición ${selected}`,
        left: "center",
      },
      tooltip: { trigger: "item" },
      series: [
        {
          type: "pie",
          radius: "50%",
          data: data,
          label: { formatter: "{b}: {c}%" },
        },
      ],
    };

    myChart.setOption(option);

    return () => {
      myChart.dispose();
    };
  }, [data, selected]);

  return (
    <div
      id="grafica-exposicion-apps"
      style={{ width: "600px", height: "400px" }}
    />
  );
};
const VistaGraficos = ({ resumen, detalle }) => {
  const [percentil, setPercentil] = useState("P50");

  const percentiles = ["P50", "P95", "P99"];
  const exposicionKeys = {
    P50: "aporte_control_p50_introducir",
    P95: "aporte_control_p95_introducir",
    P99: "aporte_control_p99_introducir",
  };

  const resultado = {
    P50: {},
    P95: {},
    P99: {},
  };

  const nombresReferencia = {
    P50: {},
    P95: {},
    P99: {},
  };

  // Agrupar por caracteristica_seguridad
  const agrupado = detalle.reduce((acc, item) => {
    const key = item.caracteristica_seguridad.toLowerCase();
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  // Para cada percentil, ordenar y tomar top 3
  percentiles.forEach((p) => {
    const exposicionKey = exposicionKeys[p];
    Object.keys(agrupado).forEach((caracteristica) => {
      const ordenado = agrupado[caracteristica]
        .sort((a, b) => b[exposicionKey] - a[exposicionKey])
        .slice(0, 3);

      resultado[p][caracteristica] = ordenado.map(
        (item) => item[exposicionKey]
      );
      nombresReferencia[p][caracteristica] = ordenado.map(
        (item) => item.nombre
      );
    });
  });
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <label>Selecciona percentil: </label>
        <select
          value={percentil}
          onChange={(e) => setPercentil(e.target.value)}
        >
          {opciones.map((op) => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </select>
      </div>

      <Row>
        <Col style={{ flex: 1 }}>
          <GraficaExposicionApps resumen={resumen} selected={percentil} />
        </Col>
        <Col style={{ flex: 1 }}>
          <GraficoComponenteRanking
            percentil={percentil}
            resultado={resultado}
            nombresReferencia={nombresReferencia}
          />
        </Col>
      </Row>
    </div>
  );
};
export default VistaGraficos;
